import { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { MUSTERILER } from '../constants/filters';

const MUSTERI_MAP = Object.fromEntries(MUSTERILER.map(m => [m.value, m.label]));

const TIP_RENKLER = {
  request:    { bg: 'bg-green-50',  border: 'border-green-300',  emoji: '📥', label: 'Request' },
  validation: { bg: 'bg-yellow-50', border: 'border-yellow-300', emoji: '⚠️', label: 'Validation' },
  process:    { bg: 'bg-blue-50',   border: 'border-blue-300',   emoji: '⚙️', label: 'Process' },
  error:      { bg: 'bg-red-50',    border: 'border-red-300',    emoji: '🔴', label: 'Error' },
};

function formatDatetime(val) {
  if (!val || val === '-') return '-';
  try {
    if (typeof val === 'string' && val.includes('T'))
      return new Date(val).toLocaleString('tr-TR');
    const num = parseFloat(val);
    const ms  = num > 9999999999 ? num : num * 1000;
    return new Date(ms).toLocaleString('tr-TR');
  } catch { return val; }
}

/**
 * Tek bir log kaydının tüm alanlarını key-value olarak gösterir.
 * _tip gibi internal alanları hariç tutar.
 */
function LogDetayAlanlari({ log }) {
  const gizle = new Set(['_tip', 'requestId']);
  const entries = Object.entries(log).filter(
    ([k, v]) => !gizle.has(k) && v !== undefined && v !== null && v !== '' && v !== '-'
  );
  if (entries.length === 0) return null;

  return (
    <div className='grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 mt-2'>
      {entries.map(([k, v]) => (
        <div key={k} className='contents'>
          <span className='text-xs text-slate-400 font-medium whitespace-nowrap'>
            {k}:
          </span>
          <span className='text-xs text-slate-600 break-all'>
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function RequestTimeline({ group, onClose }) {
  const [copied, setCopied] = useState(null); // 'requestId' | 'endpoint' | null

  if (!group) return null;

  const { requestId, request, process: processes, validation, error: errors } = group;

  // Tüm logları sabit sırayla timeline'a dönüştür:
  // 1. Request → 2. Validation → 3. Error → 4. Process
  const events = [];

  if (request) {
    events.push({ ...request, _tip: 'request' });
  }

  validation.forEach(v => events.push({ ...v, _tip: 'validation' }));
  errors.forEach(e => events.push({ ...e, _tip: 'error' }));
  processes.forEach(p => events.push({ ...p, _tip: 'process' }));

  async function kopyala(text, key) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  // Özet bilgiler
  const req = request ?? {};
  const musteriAdi = MUSTERI_MAP[req.musteri] ?? req.musteri ?? '-';

  return (
    <div className='fixed inset-y-0 right-0 w-[520px] bg-white shadow-2xl
                    border-l border-gray-200 flex flex-col z-50'>
      {/* Başlık */}
      <div className='px-5 py-4 border-b border-gray-200 shrink-0'>
        <div className='flex items-center justify-between mb-2'>
          <h2 className='font-semibold text-slate-800 text-sm'>
            İstek Zaman Çizelgesi
          </h2>
          <button onClick={onClose}
            className='p-1.5 rounded-lg hover:bg-gray-100 transition-colors'>
            <X size={18} className='text-slate-500' />
          </button>
        </div>

        {/* Request ID — tam gösterim + kopyalama */}
        <div className='flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2
                        border border-slate-200'>
          <span className='text-xs text-slate-400 shrink-0'>Request ID:</span>
          <code className='text-xs text-slate-700 font-mono break-all flex-1
                           select-all'>
            {requestId}
          </code>
          <button onClick={() => kopyala(requestId, 'requestId')}
            className='shrink-0 p-1 rounded hover:bg-slate-200 transition-colors'
            title='Request ID kopyala'>
            {copied === 'requestId'
              ? <Check size={14} className='text-green-500' />
              : <Copy size={14} className='text-slate-400' />}
          </button>
        </div>

        {/* Özet bilgi satırı */}
        <div className='flex flex-wrap gap-3 mt-3 text-xs text-slate-500'>
          {musteriAdi !== '-' && (
            <span>👤 <strong className='text-slate-700'>{musteriAdi}</strong></span>
          )}
          {req.modul && req.modul !== '-' && (
            <span>📦 <strong className='text-slate-700'>{req.modul}</strong></span>
          )}
          {req.endpoint && req.endpoint !== '-' && (
            <span className='inline-flex items-center gap-1'>
              🔗 <code className='text-slate-700'>{req.endpoint}</code>
              <button onClick={() => kopyala(req.endpoint, 'endpoint')}
                className='p-0.5 rounded hover:bg-slate-200 transition-colors'
                title='Endpoint kopyala'>
                {copied === 'endpoint'
                  ? <Check size={12} className='text-green-500' />
                  : <Copy size={12} className='text-slate-400' />}
              </button>
            </span>
          )}
          {req.httpMethod && req.httpMethod !== '-' && (
            <span className='font-bold text-slate-700'>{req.httpMethod}</span>
          )}
        </div>
      </div>

      {/* İçerik */}
      <div className='flex-1 overflow-y-auto px-5 py-4'>
        {events.length === 0 ? (
          <p className='text-center text-slate-400 py-12 text-sm'>
            Bu istek için detay bulunamadı.
          </p>
        ) : (
          <div className='relative'>
            {/* Dikey çizgi */}
            <div className='absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200' />

            <div className='space-y-3'>
              {events.map((ev, i) => {
                const tipCfg = TIP_RENKLER[ev._tip] ??
                  { bg: 'bg-gray-50', border: 'border-gray-300', emoji: '📌', label: ev._tip };

                return (
                  <div key={i} className='relative pl-10'>
                    {/* Nokta */}
                    <div className='absolute left-2 top-3 w-4 h-4 rounded-full
                                    bg-white border-2 border-gray-300
                                    flex items-center justify-center text-xs'>
                      {tipCfg.emoji}
                    </div>

                    <div className={`rounded-lg border ${tipCfg.border} ${tipCfg.bg} p-3`}>
                      {/* Üst satır: tip + zaman */}
                      <div className='flex items-center justify-between mb-1'>
                        <span className='text-xs font-semibold text-slate-700'>
                          {tipCfg.label}
                          {ev.asama && ev.asama !== '-' ? ` — ${ev.asama}` : ''}
                          {ev.islem && ev.islem !== '-' ? ` / ${ev.islem}` : ''}
                        </span>
                        <span className='text-xs text-slate-400 whitespace-nowrap'>
                          {formatDatetime(ev.zaman)}
                          {ev.sureMs && ev.sureMs !== '-' ? ` · ${ev.sureMs}ms` : ''}
                        </span>
                      </div>

                      {/* Tüm alanları göster */}
                      <LogDetayAlanlari log={ev} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Alt bilgi */}
      <div className='px-5 py-3 border-t border-gray-200 bg-slate-50 shrink-0'>
        <div className='flex items-center justify-between text-xs text-slate-400'>
          <span>{events.length} adım</span>
          <span>
            {errors.length > 0 && <span className='text-red-500 mr-2'>🔴 {errors.length} hata</span>}
            {validation.length > 0 && <span className='text-amber-500 mr-2'>⚠️ {validation.length} validasyon</span>}
            {processes.length > 0 && <span className='text-blue-500'>⚙️ {processes.length} işlem</span>}
          </span>
        </div>
      </div>
    </div>
  );
}
